import { Button, Card, Checkbox, Col, Divider, Flex, Image, Row, Tooltip, Typography, Grid, Progress } from 'antd';
import { FunctionComponent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import TextArea from 'antd/es/input/TextArea';

// components
import Icon from 'utils/icon';
import StatusComponent from 'components/status/status';
import BackButton from 'components/backButton/backButton';
import ComponentHeader from 'components/componentHeader/componentHeader';
import DisabledElement from 'components/disabledElement/disabledElement';
import FilePreview from 'components/filePreview/filePreview';
import PageTitle from 'components/title/title';
import MapComponent from '../../../../../components/map/map';

// services

// styles
import googleMeetLogo from 'assets/icons/google_meet.svg';

// models
import { Assembly, AssemblyVotingSummary } from '../models/assemblies';
import { ASSEMBLY_SUMMARY_UPDATED, assemblyChannel, isValidVotingSummary, mergeVotingSummary } from '../models/assemblyEvents';
import useAblyChannel from 'core/hooks/useAblyChannel';
import { onCloseAssembly, onEndVoting, onGetAssemblyById, onPublishAssembly } from '../services/assembliesServices';
import NotificationService from 'core/services/notification';
import LoadingComponent from 'components/loadingComponent/loadingComponent';
import { StatusColors } from 'utils/statusColors';
import { isRTL } from 'core/services/language';
import { formatFileSize } from 'utils/FileSize';
import DoughnutComponent from '../components/dougnet';
import VerificationModal from '../../verificationModal';
import { VerificationPayload } from '../../commonModels';
import dayjs from 'dayjs';
import { useTimer } from 'react-timer-hook';
import { dateFormat } from 'utils/dateFormat';

// components
import SuspendedTooltip from 'components/suspendedTooltip/suspendedTooltip';

const { useBreakpoint } = Grid;

interface ActionAssembliesViewPageProps {}

const ActionAssembliesViewPage: FunctionComponent<ActionAssembliesViewPageProps> = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { id } = useParams<{ id: string }>();
	const screens = useBreakpoint();

	const { seconds, minutes, hours, days, restart } = useTimer({
		expiryTimestamp: dayjs().toDate(),
		autoStart: false,
	});

	const [isLoading, setIsLoading] = useState(true);
	const [assembly, setAssembly] = useState<Assembly>();

	const [endVoteVerificationModalOpen, setEndVoteVerificationModalOpen] = useState(false);
	const [closeAssemblyVerificationModalOpen, setCloseAssemblyVerificationModalOpen] = useState(false);
	const [publishAssemblyVerificationModalOpen, setPublishAssemblyVerificationModalOpen] = useState(false);
	const [isPublishingAssembly, setIsPublishingAssembly] = useState(false);
	const [isEndingVoting, setIsEndingVoting] = useState(false);
	const [isClosingAssembly, setIsClosingAssembly] = useState(false);
	const [copiedOpened, setCopiedOpened] = useState(false);
	const [publicLinkCopiedOpened, setPublicLinkCopiedOpened] = useState(false);

	// read inside the realtime handler, which keeps the closure of its first render
	const statusRef = useRef<string>();
	statusRef.current = assembly?.status;

	useEffect(() => {
		getAssemblyDetails();
	}, []);

	// the summary carries no `can_*` flags and no file data, so a status change still needs a fetch
	useAblyChannel<AssemblyVotingSummary>(id ? assemblyChannel(id) : null, ASSEMBLY_SUMMARY_UPDATED, (summary) => {
		if (!isValidVotingSummary(summary)) {
			getAssemblyDetails(true);
			return;
		}

		if (statusRef.current !== summary.status) getAssemblyDetails(true);
		else setAssembly((prev) => (prev ? mergeVotingSummary(prev, summary) : prev));
	});

	const getAssemblyDetails = async (silent = false) => {
		try {
			if (!silent) setIsLoading(true);
			const response = await onGetAssemblyById(id!);

			applyAssembly(response);

			if (!silent) setIsLoading(false);
		} catch (error) {
			// a background refresh must never take the user off the page
			if (silent) return;

			navigate('/dashboard/actions/assemblies');
			NotificationService.error(t('COMMON.ERROR'), t('ACTIONS.ERROR_FETCHING_ASSEMBLY'));
		}
	};

	const applyAssembly = (response: Assembly) => {
		setAssembly(response);

		if (response.auto_publish_at && dayjs(response.auto_publish_at).isAfter(dayjs())) {
			restart(dayjs(response.auto_publish_at).toDate());
		}
	};

	const endVotingHandler = async (data: VerificationPayload) => {
		try {
			setIsEndingVoting(true);
			await onEndVoting(assembly?.id!, data);
			NotificationService.success(t('COMMON.SUCCESS'), t('ACTIONS.VOTING_ENDED_SUCCESSFULLY'));
			setEndVoteVerificationModalOpen(false);
			setIsEndingVoting(false);
			await getAssemblyDetails();
		} catch (error: any) {
			const message = error?.response?.data?.message || t('ACTIONS.ERROR_ENDING_VOTING');

			setIsEndingVoting(false);
			NotificationService.error(t('COMMON.ERROR'), message);
		}
	};

	const closeAssemblyHandler = async (data: VerificationPayload) => {
		try {
			setIsClosingAssembly(true);
			await onCloseAssembly(assembly?.id!, data);
			NotificationService.success(t('COMMON.SUCCESS'), t('ACTIONS.ASSEMBLY_CLOSED_SUCCESSFULLY'));
			setCloseAssemblyVerificationModalOpen(false);
			setIsClosingAssembly(false);
			await getAssemblyDetails();
		} catch (error: any) {
			const message = error?.response?.data?.message || t('ACTIONS.ERROR_CLOSING_ASSEMBLY');

			setIsClosingAssembly(false);
			NotificationService.error(t('COMMON.ERROR'), message);
		}
	};

	const publishAssemblyHandler = async () => {
		try {
			setIsPublishingAssembly(true);
			await onPublishAssembly(assembly?.id!);
			NotificationService.success(t('COMMON.SUCCESS'), t('ACTIONS.ASSEMBLY_PUBLISHED_SUCCESSFULLY'));
			setPublishAssemblyVerificationModalOpen(false);
			await getAssemblyDetails();
			setIsPublishingAssembly(false);
		} catch (error: any) {
			const message = error?.response?.data?.message || t('ACTIONS.ERROR_PUBLISHING_ASSEMBLY');

			setIsPublishingAssembly(false);
			NotificationService.error(t('COMMON.ERROR'), message);
		}
	};

	return (
		<>
			<PageTitle name={t('TITLE.ACTIONS')} />
			<BackButton customBack={`/dashboard/actions/assemblies`} />
			{isLoading ? (
				<LoadingComponent />
			) : (
				<>
					<Card>
						<Flex gap={20} justify="space-between" align="flex-start">
							<div>
								<Flex align="center" gap={15}>
									<h5 style={{ fontWeight: 500, marginBottom: 0 }}>#{assembly?.request_number}</h5>
									<StatusComponent text={`${t(`COMMON.${assembly?.status.toUpperCase()}`)}`} color={StatusColors[assembly?.status!]} small />
								</Flex>
								<p className="text-light">{assembly?.created_at}</p>
							</div>
							<Flex align="center" gap={10}>
								<Tooltip title={t('ACTIONS.COPIED')} color="#22b54b" open={publicLinkCopiedOpened}>
									<Button
										type="primary"
										style={{ fontSize: 14, height: 'unset', alignSelf: 'stretch' }}
										onClick={() => {
											navigator.clipboard.writeText(`${window.location.origin}/public/assembly/${assembly?.request_number}`);
											setPublicLinkCopiedOpened(true);
											setTimeout(() => setPublicLinkCopiedOpened(false), 1000);
										}}>
										<Icon name="copy" width={17} height={17} color="#fff" />
										{t('COMMON.COPY_PUBLIC_LINK')}
									</Button>
								</Tooltip>

								<Link className="btn btn-primary px-3 py-2" style={{ fontSize: 14 }} to={`/dashboard/actions/assemblies/${id}/attendance`}>
									{t('ACTIONS.ATTENDANCE')}
								</Link>
							</Flex>
						</Flex>

						<Row gutter={[16, 16]}>
							<Col span={24} md={12}>
								<DisabledElement
									value={isRTL() ? assembly?.translated_type.ar : assembly?.translated_type.en}
									label={t('ACTIONS.TYPE_OF_ASSEMBLY')}
								/>
								<DisabledElement
									value={assembly?.assembly_quorum_percentage}
									label={t('ACTIONS.ATTENDANCE_QUORUM')}
									addonAfter={<span className="px-3 fw-semibold fw-se">%</span>}
								/>
								<DisabledElement value={assembly?.date} label={t('ACTIONS.ASSEMBLY_DATE')} />
							</Col>

							<Col span={24} md={12}>
								<DoughnutComponent
									size={150}
									percentage={assembly?.assembly_quorum_percentage_data?.percentage}
									data={[+assembly?.assembly_quorum_percentage_data?.percentage!, 100 - +assembly?.assembly_quorum_percentage_data?.percentage!]}
									colors={[assembly?.assembly_quorum_percentage_data?.quorum_met ? '#78d12f' : '#e5252a', '#dedede']}
									header={
										<h6 className={`mb-0 fw-medium ${assembly?.assembly_quorum_percentage_data?.quorum_met ? 'text-success' : 'text-danger'}`}>
											{assembly?.assembly_quorum_percentage_data?.quorum_met ? t('ACTIONS.QUORUM_MET') : t('ACTIONS.QUORUM_NOT_MET')}
										</h6>
									}
									footer={
										<>
											<p className={`mb-0 fw-medium ${assembly?.assembly_quorum_percentage_data?.quorum_met ? 'text-success' : 'text-danger'}`}>
												{t('ACTIONS.ATTENDEES')}: {assembly?.assembly_quorum_percentage_data?.attendance_shareholders_count} /{' '}
												{assembly?.assembly_quorum_percentage_data?.company_shareholders_count} {t('COMMON.SHAREHOLDERS')}
											</p>

											<p className={`mb-0 fw-medium ${assembly?.assembly_quorum_percentage_data?.quorum_met ? 'text-success' : 'text-danger'}`}>
												{t('ACTIONS.OWNS')}: {assembly?.assembly_quorum_percentage_data?.attendance_shareholders_shares} /{' '}
												{assembly?.assembly_quorum_percentage_data?.total_company_shares} {t('COMMON.SHARES')}
											</p>
										</>
									}
								/>
							</Col>
						</Row>

						<Divider />

						<Row gutter={[16, 16]} className="mt-5">
							<Col span={24} md={8}>
								<DisabledElement
									label={t('ACTIONS.GENERAL_SECRETARY_NAME')}
									value={
										(isRTL() ? assembly?.general_secretary?.name : (assembly?.general_secretary?.name_en ?? assembly?.general_secretary?.name)) ||
										t('COMMON.N_A')
									}
								/>
								<Flex align="center" justify="space-between" style={{ maxWidth: 'var(--inputWidth)' }}>
									<Image src={assembly?.general_secretary?.signature!} style={{ width: 150, maxHeight: 150 }} />
								</Flex>
							</Col>

							<Col span={24} md={8}>
								<DisabledElement
									label={t('ACTIONS.VOTE_COLLECTOR_NAME')}
									value={
										(isRTL() ? assembly?.vote_collector?.name : (assembly?.vote_collector?.name_en ?? assembly?.vote_collector?.name)) ||
										t('COMMON.N_A')
									}
								/>
								<Flex align="center" justify="space-between" style={{ maxWidth: 'var(--inputWidth)' }}>
									<Image src={assembly?.vote_collector?.signature!} style={{ width: 150, maxHeight: 150 }} />
								</Flex>
							</Col>

							{assembly?.chairman && (
								<Col span={24} md={8}>
									<DisabledElement
										label={t('ACTIONS.PRESIDENT_OF_ASSEMBLY_NAME')}
										value={(isRTL() ? assembly?.chairman?.name : (assembly?.chairman?.name_en ?? assembly?.chairman?.name)) || t('COMMON.N_A')}
									/>
									<Flex align="center" justify="space-between" style={{ maxWidth: 'var(--inputWidth)' }}>
										<Image src={assembly?.chairman?.signature!} style={{ width: 150, maxHeight: 150 }} />
									</Flex>
								</Col>
							)}
						</Row>

						<Divider />

						<div>
							<h6 className="mt-4 mb-2" style={{ fontSize: '1.1rem' }}>
								{t('ACTIONS.LOCATION')}
							</h6>

							<div className="mb-3">
								{(assembly?.location_type == 'Both' || assembly?.location_type == 'Online') && (
									<Flex align="center" gap={5} className="mb-3">
										<h5 className="mb-0">&#8226;</h5>
										<h6 className="fw-medium mb-0" style={{ color: '#000' }}>
											{t('ACTIONS.ONLINE')} -
										</h6>
										<img src={googleMeetLogo} style={{ height: 25 }} />
										<Tooltip title={t('ACTIONS.COPIED')} color="#22b54b" open={copiedOpened}>
											<Button
												type="primary"
												size="small"
												style={{ marginInlineStart: 10, padding: '2px 10px', height: 'auto' }}
												onClick={() => {
													navigator.clipboard.writeText(assembly?.online_link || '');
													setCopiedOpened(true);
													setTimeout(() => setCopiedOpened(false), 1000);
												}}>
												<Icon name="copy" width={17} height={17} color="#fff" />
												{t('ACTIONS.COPY_LINK')}
											</Button>
										</Tooltip>
									</Flex>
								)}

								{(assembly?.location_type == 'Both' || assembly?.location_type == 'Physical') && (
									<>
										<Flex align="center" gap={5} className="mb-3">
											<h5 className="mb-0">&#8226;</h5>
											<h6 className="fw-medium mb-0" style={{ color: '#000' }}>
												{t('ACTIONS.PHYSICAL')}
											</h6>
										</Flex>

										<MapComponent disabled lat={+assembly?.physical_lat} lng={+assembly?.physical_lng} />

										<div className="my-3">
											<DisabledElement fullWidth label={t('ACTIONS.ADDRESS')} value={assembly?.physical_address} />

											<DisabledElement fullWidth label={t('ACTIONS.ADDRESS_DESC')} value={assembly?.physical_address_description} />
										</div>
									</>
								)}
							</div>
						</div>
					</Card>

					<Card className="mt-4">
						<ComponentHeader title={t('ACTIONS.AGENDA_FOR_MEETING')} subtitle={t('ACTIONS.ALL_FILES_TO_ASSEMBLY')} />

						{assembly?.agendas.map((agenda, index) => (
							<FilePreview
								key={index}
								name={agenda.title}
								downloadLink={agenda.download_link}
								data={formatFileSize(agenda.size_in_kilobyte)}
								className="mb-2"
							/>
						))}
					</Card>

					<Card className="mt-4">
						<ComponentHeader title={t('ACTIONS.DECISIONS')} subtitle={t('ACTIONS.ALL_DECISIONS_IN_ASSEMBLY')} />
						{assembly?.decisions.map((decision, index) => (
							<Card key={index} className="mt-4">
								<Row gutter={[16, 16]}>
									<Col span={24}>
										<Flex align="center" gap={10}>
											<h6 className="mb-0">
												{t('ACTIONS.DECISION')} ( {index + 1} )
											</h6>

											<StatusComponent
												small
												text={
													!assembly?.assembly_quorum_percentage_data?.quorum_met
														? t('ACTIONS.ASSEMBLY_QUORUM_NOT_COMPLETE')
														: decision?.decision_quorum?.quorum_met
															? t('ACTIONS.QUORUM_MET')
															: t('ACTIONS.QUORUM_NOT_MET')
												}
												color={
													!assembly?.assembly_quorum_percentage_data?.quorum_met ? 'red' : decision?.decision_quorum?.quorum_met ? 'green' : 'red'
												}
											/>
										</Flex>
									</Col>

									<Col span={24} lg={9}>
										<Typography style={{ fontSize: 15, marginBottom: 5 }}>{t('ACTIONS.DECISION_IN_AR')}</Typography>
										<TextArea disabled value={decision.translated_title.ar} autoSize={{ minRows: 4, maxRows: 4 }} />
									</Col>

									<Col span={24} lg={9}>
										<Typography style={{ fontSize: 15, marginBottom: 5 }}>{t('ACTIONS.DECISION_IN_EN')}</Typography>
										<TextArea disabled value={decision.translated_title.en} autoSize={{ minRows: 4, maxRows: 4 }} />
									</Col>

									<Col span={24} lg={6}>
										<Flex
											vertical={screens.lg}
											style={{
												flex: 1,
												flexDirection: screens.lg ? 'column' : 'row',
												alignItems: screens.lg ? 'flex-start' : 'center',
											}}
											gap={10}>
											<DisabledElement
												label={t('ACTIONS.QUORUM_PERCENTAGE')}
												value={decision.quorum_percentage}
												addonAfter={<span className="px-3 fw-semibold fw-se">%</span>}
											/>
											<Checkbox disabled checked={decision.type == 'ShareholderCount'} style={{ marginTop: screens.lg ? 0 : 15 }}>
												{t('ACTIONS.SHAREHOLDER_COUNT')}
											</Checkbox>
										</Flex>
									</Col>

									<Col span={24} md={9}>
										<DisabledElement
											fullWidth
											label={t('ACTIONS.ABSTENTION_TYPE')}
											value={t(`ACTIONS.${decision?.abstention_type?.toUpperCase()}`)}
										/>
									</Col>

									<Col span={24} md={15}>
										{decision?.abstention_type == 'specific_shareholders' && (
											<Flex
												gap={10}
												wrap
												style={{
													background: '#f9fafb',
													border: '1px solid #d9d9d9',
													padding: '3px 11px',
													borderRadius: 'var(--radius)',
													cursor: 'not-allowed',
													maxWidth: 'unset',
													marginTop: 28,
												}}>
												{decision?.abstaining_shareholders.map((shareholder, index) => (
													<p
														className="mb-0"
														style={{ fontSize: 12, background: '#f2f2f2', padding: '5px 10px', borderRadius: 5, fontWeight: 500 }}
														key={index}>
														{`${isRTL() ? shareholder.name : (shareholder.name_en ?? shareholder.name)}`}{' '}
														<StatusComponent
															small
															color={shareholder.is_board_member ? 'green' : 'blue'}
															text={shareholder.is_board_member ? t('ACTIONS.BOARD_MEMBER') : t('COMMON.SHAREHOLDER')}
															style={{ fontSize: 11, marginInlineStart: 5 }}
														/>
													</p>
												))}
											</Flex>
										)}
									</Col>

									<Divider className="my-2" />

									<Col span={24} className="mt-4">
										<Row gutter={[16, 16]}>
											<Col span={24} lg={12}>
												<h6 style={{ textAlign: 'center', fontSize: '1.1rem' }}>{t('ACTIONS.CURRENT_VOTING_RESULT')}</h6>

												<Flex justify="space-around" align="center" gap={20} wrap style={{ marginBlock: 20, borderInlineEnd: '1px solid #ccc' }}>
													<DoughnutComponent
														percentage={decision.voting_result_details?.Yes}
														data={[+decision.voting_result_details?.Yes!, 100 - +decision.voting_result_details?.Yes!]}
														colors={['#78d12f', '#dedede']}
														footer={t('ACTIONS.YES')}
													/>

													<DoughnutComponent
														percentage={decision.voting_result_details?.No}
														data={[+decision.voting_result_details?.No!, 100 - +decision.voting_result_details?.No!]}
														colors={['#ee1e40', '#dedede']}
														footer={t('ACTIONS.NO')}
													/>

													<DoughnutComponent
														percentage={decision.voting_result_details?.Abstained}
														data={[+decision.voting_result_details?.Abstained, 100 - +decision.voting_result_details?.Abstained!]}
														colors={['#2884EF', '#dedede']}
														footer={t('ACTIONS.ABSTAINED')}
													/>
												</Flex>

												{['Completed', 'Approved', 'Published'].includes(assembly?.status) && (
													<Flex
														justify="center"
														align="center"
														gap={10}
														style={{ color: decision.voting_result_details.WinningDecision ? 'var(--primary)' : 'red' }}>
														{decision.voting_result_details.WinningDecision ? (
															<>
																{t('ACTIONS.FINAL_VOTING_RESULT')}:
																<span className="fw-medium">{t(`ACTIONS.${decision?.voting_result_details?.WinningDecision?.toUpperCase()}`)}</span>
															</>
														) : (
															<span className="fw-medium">{t('ACTIONS.DECISION_FAILED')}</span>
														)}
													</Flex>
												)}
											</Col>
											<Col span={24} lg={12}>
												<h6 style={{ textAlign: 'center', fontSize: '1.1rem' }}>{t('ACTIONS.ATTENDANCE_VOTING_STATUS')}</h6>

												<Flex justify="space-around" align="center" gap={20} style={{ marginBlock: 20 }}>
													<DoughnutComponent
														percentage={decision.voting_result_details?.VoteParticipationPercentage}
														data={[
															+decision.voting_result_details?.VoteParticipationPercentage!,
															100 - +decision.voting_result_details?.VoteParticipationPercentage!,
														]}
														colors={['#78d12f', '#dedede']}
														footer={
															<div className="text-center">
																<p className="mb-1">{t('ACTIONS.VOTED_USERS')}</p>
																<p className={`mb-0 fw-medium text-success`}>
																	{t('ACTIONS.NUMBER_OF_SHAREHOLDER_ADDED_VOTES')}: {decision.voting_result_details?.VotedShareholdersCount}{' '}
																	{t('COMMON.SHAREHOLDERS')}
																</p>

																{decision.type == 'ShareCount' && (
																	<p className={`mb-0 fw-medium text-success`}>
																		{t('ACTIONS.OWNS')}: {decision.voting_result_details?.VotedShares} {t('COMMON.SHARES')}
																	</p>
																)}
															</div>
														}
													/>

													<DoughnutComponent
														percentage={decision.voting_result_details?.NoVotePercentage}
														data={[+decision.voting_result_details?.NoVotePercentage!, 100 - +decision.voting_result_details?.NoVotePercentage!]}
														colors={['#e5252a', '#dedede']}
														footer={
															<div className="text-center">
																<p className="mb-1">{t('ACTIONS.NO_VOTE')}</p>
																<p className={`mb-0 fw-medium text-danger`}>
																	{t('ACTIONS.NUMBER_OF_SHAREHOLDER_ADDED_NO_VOTES')}: {decision.voting_result_details?.NotVotedShareholdersCount}{' '}
																	{t('COMMON.SHAREHOLDERS')}
																</p>

																{decision.type == 'ShareCount' && (
																	<p className={`mb-0 fw-medium text-danger`}>
																		{t('ACTIONS.OWNS')}: {decision.voting_result_details?.NotVotedShares} {t('COMMON.SHARES')}
																	</p>
																)}
															</div>
														}
													/>
												</Flex>
											</Col>
										</Row>
									</Col>
								</Row>
							</Card>
						))}
					</Card>

					{(assembly?.status == 'Completed' || assembly?.status == 'Approved' || assembly?.status == 'Published') && (
						<Card className="mt-4">
							<Divider />

							<ComponentHeader title={t('ACTIONS.ASSEMBLY_FILES')} subtitle={t('ACTIONS.DOWNLOAD_ASSEMBLY_FILES')} />

							<p className="mb-0">{t('ACTIONS.COMPLETION_PROGRESS')}</p>
							<Progress percent={assembly?.files_progress_percentage} strokeColor={{ '0%': '#108ee9', '100%': '#87d068' }} />
							<p className="text-center">
								{t('ACTIONS.AVAILABLE_FILES', {
									num: assembly?.files_generated_count,
								})}
							</p>

							<Row gutter={[16, 16]} className="mt-5">
								<Col span={24} lg={12}>
									<FilePreview
										name={t('ACTIONS.ATTENDANCE_PDF')}
										disabled={!assembly?.attendance_pdf_path?.download_link}
										downloadLink={assembly?.attendance_pdf_path?.download_link}
									/>
								</Col>
								<Col span={24} lg={12}>
									<FilePreview
										name={t('ACTIONS.DECISIONS_RESULTS_PDF')}
										disabled={!assembly?.decisions_results_pdf_path?.download_link}
										downloadLink={assembly?.decisions_results_pdf_path?.download_link}
									/>
								</Col>
								<Col span={24} lg={12}>
									<FilePreview
										name={t('ACTIONS.SHAREHOLDER_VOTING_CARDS')}
										disabled={!assembly?.shareholder_voting_cards_path?.download_link}
										downloadLink={assembly?.shareholder_voting_cards_path?.download_link}
									/>
								</Col>
								<Col span={24} lg={12}>
									<FilePreview
										name={t('ACTIONS.DECISIONS_COMMENTS_PDF')}
										disabled={!assembly?.decisions_comments_pdf_path?.download_link}
										downloadLink={assembly?.decisions_comments_pdf_path?.download_link}
									/>
								</Col>
							</Row>
						</Card>
					)}

					{assembly?.status == 'Approved' && assembly.auto_publish && assembly.can_publish && (
						<Card className="mt-4">
							<ComponentHeader title={t('ACTIONS.AUTO_PUBLISH')} />

							<h6 className="text-center text-danger fw-600">
								{days} {t('ACTIONS.DAYS')} {hours} {t('ACTIONS.HOURS')} {minutes} {t('ACTIONS.MINUTES')} {seconds} {t('ACTIONS.SECONDS')}
							</h6>

							<h6 className="text-center text-danger fw-500">
								{t('ACTIONS.AUTO_PUBLISH_TIME')}: {dateFormat(assembly.auto_publish_at!)}
							</h6>
						</Card>
					)}

					<Flex justify="flex-end" align="center" className="mt-5">
						{assembly?.can_close_voting && (
							<SuspendedTooltip>
								<Button type="primary" size="middle" onClick={() => setEndVoteVerificationModalOpen(true)}>
									{t('ACTIONS.CLOSE_VOTING')}
								</Button>
							</SuspendedTooltip>
						)}

						{assembly?.can_close_assembly && (
							<SuspendedTooltip>
								<Button type="primary" danger size="middle" onClick={() => setCloseAssemblyVerificationModalOpen(true)}>
									{t('ACTIONS.CLOSE_ASSEMBLY')}
								</Button>
							</SuspendedTooltip>
						)}

						{assembly?.can_publish && (
							<SuspendedTooltip>
								<Button type="primary" size="middle" onClick={publishAssemblyHandler} loading={isPublishingAssembly}>
									{t('ACTIONS.PUBLISH')}
								</Button>
							</SuspendedTooltip>
						)}
					</Flex>
				</>
			)}

			{/* END VOTE VERIFICATION MODAL */}
			<VerificationModal
				open={endVoteVerificationModalOpen}
				isLoading={isEndingVoting}
				onCancel={() => setEndVoteVerificationModalOpen(false)}
				onVerificationSuccess={endVotingHandler}
				module="assembly"
			/>

			{/* CLOSING ASSEMBLY VERIFICATION MODAL */}
			<VerificationModal
				open={closeAssemblyVerificationModalOpen}
				isLoading={isClosingAssembly}
				onCancel={() => setCloseAssemblyVerificationModalOpen(false)}
				onVerificationSuccess={closeAssemblyHandler}
				module="assembly"
			/>
		</>
	);
};

export default ActionAssembliesViewPage;
